<template>
  <div :class="containerClass" class="relative">
    <!-- Loading State -->
    <div v-if="loading" class="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></div>
    
    <!-- Media Display -->
    <video
      v-if="secureImageUrl && shouldRenderVideo"
      :src="secureImageUrl"
      :class="imageClass"
      @loadeddata="onMediaLoad"
      @error="onMediaError"
      @click="$emit('click')"
      ref="videoRef"
      class="w-full h-full"
      :autoplay="isPreview || forceAutoplay"
      :muted="muted"
      :loop="loop"
      :playsinline="playsinline"
    />
    <img
      v-else-if="secureImageUrl"
      :src="secureImageUrl"
      :alt="alt"
      :class="imageClass"
      @load="onMediaLoad"
      @error="onMediaError"
      @click="$emit('click')"
      ref="imageRef"
      class="w-full h-full"
    />
    
    <!-- Error State -->
    <div v-else-if="error" class="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center rounded">
      <span class="text-gray-500 text-sm">Failed to load</span>
    </div>
    
    <!-- Preview Indicator -->
    <div v-if="isPreview && !loading" class="absolute bottom-1 right-1 bg-black/20 text-white/70 text-xs px-1 rounded">
      Preview
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { centralizedImageSecurity, transformCloudinaryUrl } from '../../services/centralizedImageSecurity';
import { isLikelyVideoUrl } from '../../utils/validation';

interface Props {
  imageUrl: string;
  wallpaperId: string;
  isPreview?: boolean;
  alt?: string;
  containerClass?: string;
  imageClass?: string;
  targetAspect?: number; // desired aspect ratio (w/h)
  targetWidth?: number;  // suggested pixel width for Cloudinary
  targetHeight?: number; // optional pixel height
  cropMode?: 'fill' | 'fit' | 'pad';
  // Live wallpaper props
  forceAutoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsinline?: boolean;
}

interface Emits {
  (e: 'click'): void;
  (e: 'load'): void;
  (e: 'error'): void;
}

const props = withDefaults(defineProps<Props>(), {
  isPreview: true,
  alt: 'Wallpaper',
  containerClass: '',
  imageClass: '',
  cropMode: 'fill',
  forceAutoplay: false,
  muted: true,
  loop: true,
  playsinline: true
});

const emit = defineEmits<Emits>();

// Reactive state
const loading = ref(true);
const error = ref(false);
const secureImageUrl = ref<string>('');
const imageRef = ref<HTMLImageElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const isVideoUrl = ref(false);
const isPosterFrameUrl = (url: string): boolean => /f_jpg|f_png|f_webp/i.test(url) || /so_0,.*f_jpg/i.test(url);
const shouldRenderVideo = computed(() => isVideoUrl.value && !isPosterFrameUrl(props.imageUrl));

// Load secure image
const loadSecureImage = async () => {
  if (!props.imageUrl || !props.wallpaperId) {
    error.value = true;
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = false;
    
    // Check if this is a poster frame URL (generated from video but returns an image)
    // Poster frame URLs contain transformations like f_jpg or so_0,...
    const isPosterFrame = isPosterFrameUrl(props.imageUrl);
    
    // Only treat as video if it's a video URL AND not a poster frame
    isVideoUrl.value = isLikelyVideoUrl(props.imageUrl) && !isPosterFrame;

    // For poster frames or regular images: apply Cloudinary transformations
    // For raw videos: pass through without transformations (security service handles videos separately)
    const transformedUrl = (isVideoUrl.value || isPosterFrame)
      ? props.imageUrl
      : transformCloudinaryUrl(props.imageUrl, {
          width: props.targetWidth,
          height: props.targetHeight,
          aspectRatio: props.targetAspect,
          cropMode: props.cropMode
        });

    const result = await centralizedImageSecurity.getSecureImageUrl({
      imageUrl: transformedUrl,
      wallpaperId: props.wallpaperId,
      isPreview: props.isPreview
    });

    secureImageUrl.value = result.secureUrl;
    
    if (!result.isSecure && props.isPreview) {
      console.warn('[SimpleSecureImage] Using fallback URL for preview');
    }

  } catch (err) {
    console.error('[SimpleSecureImage] Failed to load secure image:', err);
    error.value = true;
  } finally {
    loading.value = false;
  }
};

// Media event handlers
const onMediaLoad = () => {
  loading.value = false;
  
  // Apply security protections
  if (imageRef.value) {
    centralizedImageSecurity.protectImageElement(imageRef.value);
  } else if (videoRef.value) {
    centralizedImageSecurity.protectVideoElement(videoRef.value);
  }
  
  emit('load');
};

const onMediaError = () => {
  loading.value = false;
  error.value = true;
  emit('error');
};

// Watch for prop changes
watch([() => props.imageUrl, () => props.wallpaperId], () => {
  loadSecureImage();
}, { immediate: false });

// Initialize
onMounted(() => {
  loadSecureImage();
});
</script>

<style scoped>
/* Prevent image extraction */
img {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
}

video {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
}
</style>